/*
	OneSwatches 2.2.0 Usage (Webpack)
	Yang @onerockwell
	Adding option availablity check

	OneSwatches 2.1.0 Usage
	Yang @onerockwell
	Adding option availablity check

	OneSwatches 2.0.0 Usage
	Yang @onerockwell

	OneSwatches 1.0.0 Usage
	Yang & Patrick @onerockwell
*/

// Import all dependencies here
import _ from 'underscore';
import Backbone from 'backbone';
import { publish, subscribe, PUB_SUB_EVENTS } from '@/lib/pubsub';

import '../../styles/modules/oneSwatch.scss';

// Define the Backbone View here (Optional)
let OneProductFormView = Backbone.View.extend({

    /**
     * @param {Boolean} settings.preSelect Pre-select the first variant
     * @param {Boolean} settings.updateURL Update the "variant" URL parameter on variant change
     */
	initialize: function(settings) {

        this.settings = settings || {};

        // Master ID input is disabled by default to give <noscript> form priority
        this.el.querySelector('[name="id"]').disabled = false;

        this.userIsPro = this.el.dataset.userIsPro === 'true';
        this.proRegisterUrl = this.el.dataset.proRegisterUrl || '/pages/create-a-professional-account';
        this.copy = this.readHalfPackCopy();

        this.setVariant();
        this.onVariantChange();

        this.el.addEventListener('submit', e => this.onFormSubmit(e));
	},

	events: {
		'change': 'onVariantChange',
		'click input[data-ghost-option]': 'onGhostOptionClick',
	},

    // Flex Weft 2.0: a ghost Half Pack option (rendered by product-swatches.liquid when this
    // shade has no real Half Pack variant) must never actually become selected. Blocking the
    // click here stops the radio from checking in the first place, so 'change' never fires and
    // none of the variant-selection/notice/pubsub flow runs — Full Pack stays selected as-is.
    onGhostOptionClick: function(e) {
        e.preventDefault();
    },

	onFormSubmit: function(e) {
        e.preventDefault();

        this.handleErrorMessage();

        // Half Pack is PRO-exclusive: send non-PRO customers to apply instead of adding to cart.
        if (this.isProGatedSelection) {
            window.location.href = this.proRegisterUrl;
            return;
        }

        // Selection has no matching variant (e.g. Half Pack + 16 IN): never fall back to a stale id.
        if (this.isNonexistentSelection) {
            this.handleErrorMessage('This combination is unavailable. Please choose a different option.');
            return;
        }

        const variantId = this.currentVariant ? this.currentVariant.id : Number(this.el.querySelector('input[name="id"]')?.value);
        const quantity = Number(this.el.elements.quantity.value);

        // Check if this is an education product (with kit)
        const educationKitVariantId = this.el.dataset.eduKitVariantId;

        const formJson = {
            items: []
        };

        if (educationKitVariantId) {
            // Education product - create unique link ID and add both product + kit
            const educationLinkId = crypto.randomUUID();

            // Add education product
            formJson.items.push({
                id: variantId,
                quantity: quantity,
                properties: {
                    '_education_link_id': educationLinkId
                }
            });

            // Add education kit
            formJson.items.push({
                id: Number(educationKitVariantId),
                quantity: quantity,
                properties: {
                    '_linked_education': variantId,
                    '_education_link_id': educationLinkId,
                    '_EDU_KIT': 'true'
                }
            });
        } else {
            // Regular product - default flow
            formJson.items.push({
                id: variantId,
                quantity: quantity
            });
        }

        const addOnCheckboxProductData = this.getAddOnCheckboxProductData();
        const addOnCheckboxChecked = this.el.closest('.product-core-inner').querySelector('.add-on-product-checkbox')?.checked;

        if (addOnCheckboxProductData && addOnCheckboxChecked) {
            formJson.items.push({
                id: addOnCheckboxProductData.id,
                quantity: 1
            });
        }

        const formData = new FormData(this.el);
        const config = {
            method: 'POST',
            credentials: "include",
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
            },
            body: JSON.stringify(formJson)
        };

        fetch('/cart/add.js', config)
            .then(response => response.json())
            .then(response => {
                if (response.status) {
                    publish(PUB_SUB_EVENTS.cartError, {
                        errors: response.errors || response.description,
                        message: response.message
                    });
                    this.handleErrorMessage(response.description);
                } else {
                    publish(PUB_SUB_EVENTS.cartUpdate, {...response, cartEvent: 'pdpAdd'});

                    // Check bridal kit discount after cart update
                    if (window.BridalKitDiscount) {
                        window.BridalKitDiscount.check();
                    }
                }
            });
    },

    handleErrorMessage: function(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessage =
            this.errorMessage || this.el.querySelector('.form-errors');
        if (!this.errorMessage) return;

        this.errorMessage.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
            this.errorMessage.textContent = errorMessage;
        }
    },

    onVariantChange: function(e) {
        this.updateOptions();
        this.resolveNonexistentSelection(e);
        this.updateMasterId();
        this.updateVariantStatuses();
        this.updateProGating();
        this.updateNonexistentNotice();
        this.updateAddButton();
        this.updatePromoText();
        this.updateSubtext();
        this.updateBadge();

        if (this.currentVariant) {
            this.updateVariantInput();
            this.settings.updateURL && this.updateURL();
            /* sets the afterpay installment amt if selected variant exist and afterpay message exist in PDP */
            this.updateAfterPayInstallmentAmt()
        } else if (this.isNonexistentSelection) {
            // Don't leave the previous variant's id sitting in the form (the sticky ATC reads it too)
            this.el.querySelector('input[name="id"]').value = '';
        }
    },

    /**
     * Some option combinations (e.g. Half Pack + 16 IN) have no matching variant at all —
     * different from a real variant that's simply out of stock. When that happens:
     *  - if the primary/constraining option (e.g. Pack) is what just changed, silently
     *    snap the other option(s) to a value that actually exists (Half Pack -> 20 IN).
     *  - if the constrained option (Length) was changed into an invalid value (e.g. clicking
     *    a crossed-out 16 IN while Half Pack is selected), leave it selected so
     *    updateNonexistentNotice can prompt the customer to resolve it themselves.
     */
    resolveNonexistentSelection: function(e) {
        this.isNonexistentSelection = false;

        const fieldsets = Array.from(this.el.querySelectorAll('fieldset'));
        const visibleFieldsets = fieldsets.filter(fieldset => !fieldset.hasAttribute('hidden'));
        if (visibleFieldsets.length < 2) return;

        const variants = this.getVariantData();
        const matchesCurrentSelection = variant => this.options.every((value, index) => variant.options[index] === value);
        if (variants.some(matchesCurrentSelection)) return;

        const changedFieldset = e && e.target && e.target.closest('fieldset');
        const primaryFieldset = this.getPrimaryFieldset();

        if (changedFieldset && changedFieldset !== primaryFieldset) {
            this.isNonexistentSelection = true;
            return;
        }

        const changedIndex = fieldsets.indexOf(changedFieldset || primaryFieldset);
        if (!this.snapToClosestVariant(changedIndex)) {
            this.isNonexistentSelection = true;
            return;
        }

        this.updateOptions();
    },

    // The "Length" fieldset is the constrained option; the other visible fieldset (e.g. Pack)
    // is the primary/constraining one whose choice silently corrects Length, rather than the
    // other way around. Identified by class, not DOM position, since option order isn't guaranteed.
    getPrimaryFieldset: function() {
        const visibleFieldsets = Array.from(this.el.querySelectorAll('fieldset:not([hidden])'));
        const lengthFieldset = this.el.querySelector('.oneswatch-length');
        return visibleFieldsets.find(fieldset => fieldset !== lengthFieldset) || visibleFieldsets[0];
    },

    // Finds a variant that keeps the value at anchorIndex, then checks its values for
    // every OTHER option (regardless of DOM order). Returns false if no such variant exists.
    snapToClosestVariant: function(anchorIndex) {
        const fieldsets = Array.from(this.el.querySelectorAll('fieldset'));
        const variants = this.getVariantData();
        const anchorValue = this.options[anchorIndex];
        const closestMatch = variants.find(variant => variant.options[anchorIndex] === anchorValue);
        if (!closestMatch) return false;

        closestMatch.options.forEach((value, index) => {
            if (index === anchorIndex) return;
            const input = fieldsets[index]?.querySelector(`input[value="${CSS.escape(value)}"]`);
            if (input) input.checked = true;
        });
        return true;
    },

    // Merch-editable copy for the two Half Pack notices (Theme Settings > "Flex Weft 2.0 —
    // Half Pack Messaging"). Liquid only renders the settings-sourced JSON for
    // product.type == 'Flex Weft 2.0' (see product-core-form.liquid); every other product
    // falls back to these same defaults, so behavior is unchanged anywhere else.
    readHalfPackCopy: function() {
        const defaults = {
            lengthNotice: {
                title: 'Length not offered in the Half Pack',
                body: 'Half Pack is available in {length}. Full Pack keeps all lengths.',
                cta: 'Use {length}'
            },
            proNotice: {
                title: 'Half Pack is professional exclusive',
                body: 'Full Pack is available to everyone at retail. The Half Pack is sold to licensed stylists only, at PRO pricing.',
                cta: 'Apply for a PRO account'
            }
        };

        const json = this.el.querySelector('.half-pack-copy-json');
        if (!json) return defaults;

        try {
            const parsed = JSON.parse(json.textContent);
            return {
                lengthNotice: { ...defaults.lengthNotice, ...parsed.lengthNotice },
                proNotice: { ...defaults.proNotice, ...parsed.proNotice }
            };
        } catch (e) {
            return defaults;
        }
    },

    // Substitutes the dynamically-resolved Half Pack length into merch copy wherever the
    // {length} token appears. The length itself always comes from real variant data
    // (validLengths in updateNonexistentNotice) — never stored or hardcoded in copy.
    applyLengthToken: function(template, length) {
        return String(template || '').split('{length}').join(length);
    },

    // Half Pack is PRO-exclusive: shows/hides the "professional exclusive" notice based on
    // whether Half Pack is selected and the customer lacks the PRO (beautyalist) tag. Only
    // applies to a selectable combination — an invalid length (no matching variant) shows the
    // same "length not offered" notice for guests and PRO customers alike; gating only kicks
    // in once they land on a real Half Pack variant.
    updateProGating: function() {
        const primaryChecked = this.getPrimaryFieldset()?.querySelector(':checked');
        this.isHalfPackSelected = !!primaryChecked && /half pack/i.test(primaryChecked.value);
        this.isProGatedSelection = this.isHalfPackSelected && !this.userIsPro && !this.isNonexistentSelection;

        const scope = this.el.closest('.product-core-inner') || this.el;
        let notice = scope.querySelector('.pro-gate-notice');

        if (!this.isProGatedSelection) {
            if (notice) notice.hidden = true;
            return;
        }

        if (!notice) {
            notice = document.createElement('div');
            notice.className = 'pro-gate-notice';
            notice.innerHTML = `
                <p class="pro-gate-notice__title">${this.copy.proNotice.title}</p>
                <p class="pro-gate-notice__body">${this.copy.proNotice.body}</p>
                <a class="pro-gate-notice__button" href="${this.proRegisterUrl}">${this.copy.proNotice.cta}</a>
            `;

            const lengthFieldset = this.el.querySelector('.oneswatch-length');
            (lengthFieldset || this.el).insertAdjacentElement('afterend', notice);
        }

        notice.hidden = false;
    },

    // Shows/hides the "Length not offered in the Half Pack" notice. Content is rebuilt from
    // the actual variant data every time (not hardcoded to "20 IN") — a Pack value can have
    // more than one real length, and the copy + "Use X" buttons need to reflect whichever
    // length(s) genuinely exist for the currently selected Pack.
    updateNonexistentNotice: function() {
        const scope = this.el.closest('.product-core-inner') || this.el;
        let notice = scope.querySelector('.nonexistent-notice');

        if (!this.isNonexistentSelection) {
            if (notice) notice.hidden = true;
            return;
        }

        const fieldsets = Array.from(this.el.querySelectorAll('fieldset'));
        const primaryFieldset = this.getPrimaryFieldset();
        const primaryIndex = fieldsets.indexOf(primaryFieldset);
        const lengthFieldset = this.el.querySelector('.oneswatch-length');
        const lengthIndex = fieldsets.indexOf(lengthFieldset);

        const selectedPackValue = this.options[primaryIndex];

        const validLengths = [...new Set(
            this.getVariantData()
                .filter(variant => variant.options[primaryIndex] === selectedPackValue)
                .map(variant => variant.options[lengthIndex])
        )];
        const lengthList = validLengths.length > 1
            ? `${validLengths.slice(0, -1).join(', ')} and ${validLengths[validLengths.length - 1]}`
            : validLengths[0];

        const titleText = this.applyLengthToken(this.copy.lengthNotice.title, lengthList);
        const bodyText = validLengths.length
            ? this.applyLengthToken(this.copy.lengthNotice.body, lengthList)
            : `${selectedPackValue} isn't available in this length.`;

        const useButtonsHtml = validLengths.map(length => {
            const safeLength = String(length).replace(/"/g, '&quot;');
            const ctaText = this.applyLengthToken(this.copy.lengthNotice.cta, length);
            return `<button type="button" class="nonexistent-notice__button" data-length="${safeLength}">${ctaText}</button>`;
        }).join(' ');

        if (!notice) {
            notice = document.createElement('div');
            notice.className = 'nonexistent-notice';
            (lengthFieldset || this.el).insertAdjacentElement('afterend', notice);
        }

        notice.innerHTML = `
            <p class="nonexistent-notice__title">${titleText}</p>
            <p class="nonexistent-notice__body">${bodyText}</p>
            ${useButtonsHtml}
        `;
        notice.querySelectorAll('.nonexistent-notice__button').forEach(button => {
            button.addEventListener('click', () => this.useSuggestedVariant(button.dataset.length));
        });

        notice.hidden = false;
    },

    // "Use X": keeps the constraining option (Pack) as-is. With a specific length, selects
    // it directly (from the notice's "Use X" buttons); otherwise falls back to the closest
    // real variant for the current Pack value. Then re-runs the full variant-change pipeline.
    useSuggestedVariant: function(length) {
        const fieldsets = Array.from(this.el.querySelectorAll('fieldset'));
        const lengthFieldset = this.el.querySelector('.oneswatch-length');

        if (length && lengthFieldset) {
            const input = lengthFieldset.querySelector(`input[value="${CSS.escape(length)}"]`);
            if (input) input.checked = true;
        } else {
            const primaryIndex = fieldsets.indexOf(this.getPrimaryFieldset());
            this.snapToClosestVariant(primaryIndex);
        }

        this.onVariantChange();
    },

	/**
     *  Mark radios as checked if a variant is present in the URL params or if
     *  preSelect setting is true
     */
    setVariant: function() {
        const params = new URLSearchParams(window.location.search);
        const variantId = this.settings.preSelect ?
            this.getVariantData()[0].id :
            params.get('variant');

        if (variantId) {
            this.currentVariant = this.getVariantData().find(variant => {
                return variant.id.toString() === variantId.toString();
            });
            if (this.currentVariant) {
                this.currentVariant.options.forEach(option => {
                    this.el.querySelector(`input[value="${option}"]`).checked = true;
                });
            }
        }
    },

    updateAddButton: function() {
        this.numOptions = this.numOptions || this.el.querySelectorAll('fieldset').length;
        const addButton = this.el.querySelector('[name="add"]');
        const stickyAddButton = document.getElementById('sticky_atc');
        const addButtonText = this.el.querySelector('[name="add"] > .add-to-cart-text');
        const customSkuThresholds = {"143001":50,"143003":50,"709012":20,"709014":20,"709017":20,"709021":20,"709031":20,"709036":20,"709037":20,"709038":20,"709039":20,"709043":20,"709045":20,"709056":20,"709057":20,"709069":20,"709071":20,"710012":20,"710014":20,"710017":20,"710021":20,"710031":20,"710036":20,"710037":20,"710038":20,"710039":20,"710043":20,"710045":20,"710056":20,"710057":20,"710069":20,"710071":20,"711269":50,"711270":50,"717012":20,"717014":20,"717017":20,"717021":20,"717031":20,"717036":20,"717037":20,"717038":20,"717039":20,"717043":20,"717045":20,"717056":20,"717057":20,"717069":20,"717071":20,"759009":50,"775012":5,"775014":5,"775017":5,"775021":5,"775031":5,"775036":5,"775037":5,"775038":5,"775039":5,"775043":5,"775045":5,"775056":5,"775057":5,"775069":5,"775071":5,"776012":5,"776014":5,"776017":5,"776021":5,"776031":5,"776036":5,"776037":5,"776038":5,"776039":5,"776043":5,"776045":5,"776056":5,"776057":5,"776069":5,"776071":5,"777012":5,"777014":5,"777017":5,"777021":5,"777031":5,"777036":5,"777037":5,"777038":5,"777039":5,"777043":5,"777045":5,"777056":5,"777057":5,"777069":5,"777071":5,"785012":5,"785014":5,"785017":5,"785021":5,"785031":5,"785036":5,"785037":5,"785038":5,"785039":5,"785043":5,"785045":5,"785056":5,"785057":5,"785069":5,"785071":5,"786012":5,"786014":5,"786017":5,"786021":5,"786031":5,"786036":5,"786037":5,"786038":5,"786039":5,"786043":5,"786045":5,"786056":5,"786057":5,"786069":5,"786071":5,"787012":5,"787014":5,"787017":5,"787021":5,"787031":5,"787036":5,"787037":5,"787038":5,"787039":5,"787043":5,"787045":5,"787056":5,"787057":5,"787069":5,"787071":5};
        if (!addButton) return;

        // Longer CTA copy only ("PRO Account Required" / "Length Not in Half Pack") gets a
        // smaller mobile font via this modifier — reset here, added back in those two branches.
        addButtonText?.classList.remove('add-to-cart-text--compact');

        // Half Pack is PRO-exclusive: keep the button enabled so it's clickable, but repurpose
        // it to send the customer to apply for a PRO account instead of adding to cart.
        if (this.isProGatedSelection) {
            addButton.removeAttribute('disabled');
            stickyAddButton.removeAttribute('disabled');
            if (addButtonText) {
                addButtonText.classList.add('add-to-cart-text--compact');
                if (!addButtonText.textContent.toLowerCase().includes('pro account required')) {
                    addButtonText.textContent = 'PRO Account Required';
                }
            }
            return;
        }

        if (!this.currentVariant) {
            addButton.setAttribute('disabled', 'disabled');
            stickyAddButton.setAttribute('disabled', 'disabled');
            if (this.isNonexistentSelection) {
                if (addButtonText) {
                    addButtonText.classList.add('add-to-cart-text--compact');
                    if (!addButtonText.textContent.toLowerCase().includes('length not in half pack')) {
                        addButtonText.textContent = 'Length Not in Half Pack';
                    }
                }
            } else if (this.options?.length < this.numOptions) {
                if (addButtonText && !addButtonText.textContent.toLowerCase().includes("sold out")) {
                    addButtonText.textContent = 'Sold Out';
                }
            } else if (addButtonText && !addButtonText.textContent.toLowerCase().includes("unavailable")) {
                addButtonText.textContent = 'Unavailable';
            }
            return;
        }

        const variantSKU = this.currentVariant.sku;
        let variantQty = this.currentVariant.inventory_quantity;
        let inventory_policy = this.currentVariant.inventory_policy;
        getAccurateInventory(this.currentVariant).then(qty => {
            if (qty !== null) {
                variantQty = qty;
                console.log("✅ Accurate qty:", variantQty);
            } else {
                console.log("⚠️ Falling back to legacy qty:", variantQty);
            }
            console.log(variantSKU + " variantQty: " + variantQty + " / oos_threshold: " + oos_threshold + " / " + inventory_policy);

            if (this.currentVariant && this.currentVariant.available && variantQty > oos_threshold || this.currentVariant.available && inventory_policy == 'continue') {
                addButton.removeAttribute('disabled');
                stickyAddButton.removeAttribute('disabled');
                // addButtonText.textContent = 'Add to Cart';
                if(addButton.classList.contains('pre-order')){
                    if(addButtonText && !addButtonText.textContent.toLowerCase().includes("pre order")){
                        addButtonText.textContent = 'Pre Order';
                    }
                } else {
                    if(addButtonText && !addButtonText.textContent.toLowerCase().includes("add to cart")){
                        addButtonText.textContent = 'Add to Cart';
                    }
                }
                // DD-3144 Changes for custom threshold
                if( Shopify.country == "US" && customSkuThresholds[variantSKU] && variantQty <= customSkuThresholds[variantSKU] ) {
                    addButton.setAttribute('disabled', 'disabled');
                    stickyAddButton.setAttribute('disabled', 'disabled');
                    if (addButtonText && !addButtonText.textContent.toLowerCase().includes("sold out")) {
                        addButtonText.textContent = 'Sold Out';
                    }
                }
            } else if (this.currentVariant && variantQty <= oos_threshold){
                addButton.setAttribute('disabled', 'disabled');
                stickyAddButton.setAttribute('disabled', 'disabled');
                if(addButtonText && !addButtonText.textContent.toLowerCase().includes("sold out")){
                    addButtonText.textContent = 'Sold Out';
                }
            } else if (this.currentVariant && !this.currentVariant.available){
                addButton.setAttribute('disabled', 'disabled');
                stickyAddButton.setAttribute('disabled', 'disabled');
                if(addButtonText && !addButtonText.textContent.toLowerCase().includes("sold out")){
                    addButtonText.textContent = 'Sold Out';
                }
            } else {
                addButton.setAttribute('disabled', 'disabled');
                stickyAddButton.setAttribute('disabled', 'disabled');
                if(this.options?.length < this.numOptions){
                    if(addButtonText && !addButtonText.textContent.toLowerCase().includes("sold out")){
                        addButtonText.textContent = 'Sold Out';
                    }
                } else{
                    if(addButtonText && !addButtonText.textContent.toLowerCase().includes("unavailable")){
                        addButtonText.textContent = 'Unavailable';
                    }
                }
            }

        });
    },

    updateOptions: function() {
        const fieldsets = Array.from(this.el.querySelectorAll('fieldset'));
        this.options = fieldsets.map(fieldset => {
            // Variables for variant specs
            let variant_specs_html = '';
            let variant_specs = document.getElementById('variant_specs');
            let variant = Array.from(fieldset.querySelectorAll('input')).find(input => input.checked);

            if(variant?.name == 'Length') {
              // Assigning the designated variant specs
              let specs_arr = [variant.dataset.length, variant.dataset.weight, variant.dataset.style, variant.dataset.pieces];
              // Removing empty strings from array
              specs_arr = specs_arr.filter(spec => spec !== '');
              // Constructing string with all available specs
              for(let i = 0; i < specs_arr.length; i++) {
                if(specs_arr[i] !== '') variant_specs_html += `${specs_arr[i]} ${[i] == specs_arr.length - 1 ? '' : '|' } `
              }
              // Setting variant specs element html
              variant_specs.innerHTML = variant_specs_html;
            }

            return Array.from(fieldset.querySelectorAll('input')).find(input => input.checked)?.value;
        }).filter(Boolean)
        publish(PUB_SUB_EVENTS.variantOptionsChange, this.options);
    },

    updateMasterId: function() {
        this.currentVariant = this.getVariantData().find(variant => {
            // Assumes that options appear in the same order as they do in the DOM
            return !variant.options
                .map((option, index) => {
                    return this.options[index] === option;
                })
                .includes(false);
        });
        if (this.currentVariant) publish(PUB_SUB_EVENTS.variantChange, this.currentVariant);
    },

    // Mark radios as disabled depending on currently selected values
    updateVariantStatuses: function() {
        const inputWrappers = [...this.el.querySelectorAll('fieldset')];

        // Visible fieldsets are multi-value options rendered as swatch buttons.
        // Hidden fieldsets are single-value options (always checked, never shown).
        const visibleFieldsets = inputWrappers.filter(fs => !fs.hasAttribute('hidden'));
        if (!visibleFieldsets.length) return;

        // A variant is purchasable if it matches the same oos_threshold logic the ATC button uses:
        // available AND (inventory_policy=continue OR qty > threshold)
        const isEffectivelyAvailable = v =>
            v.available && (v.inventory_policy === 'continue' || v.inventory_quantity > oos_threshold);

        // Single visible option (e.g. Length only): mark OOS values directly from variant availability.
        if (visibleFieldsets.length === 1) {
            const optionIndex = inputWrappers.indexOf(visibleFieldsets[0]) + 1;
            const optionInputs = [...visibleFieldsets[0].querySelectorAll('input[type="radio"]')];
            const availableValues = this.getVariantData().filter(isEffectivelyAvailable).map(v => v[`option${optionIndex}`]);
            this.setInputAvailability(optionInputs, availableValues);
            return;
        }

        // Two visible options (e.g. Pack + Length) constrain each other symmetrically:
        // whichever one is picked, the other's invalid values get crossed out — regardless
        // of which option was defined first on the product.
        if (visibleFieldsets.length === 2) {
            visibleFieldsets.forEach(fieldset => {
                const index = inputWrappers.indexOf(fieldset);
                const optionInputs = [...fieldset.querySelectorAll('input[type="radio"]')];
                if (!optionInputs.length) return;

                const otherFieldset = visibleFieldsets.find(fs => fs !== fieldset);
                const otherIndex = inputWrappers.indexOf(otherFieldset);
                const otherChecked = otherFieldset.querySelector(':checked');

                const matchingVariants = otherChecked
                    ? this.getVariantData().filter(variant => variant[`option${otherIndex + 1}`] === otherChecked.value)
                    : this.getVariantData();

                const availableValues = matchingVariants.filter(isEffectivelyAvailable).map(v => v[`option${index + 1}`]);
                this.setInputAvailability(optionInputs, availableValues);
            });
            return;
        }

        // 3+ visible options: the first visible fieldset is the primary selector (e.g. Color).
        // We skip it — we can't know its OOS state without something preceding it.
        // All subsequent visible fieldsets (e.g. Length) are evaluated based on the full
        // selection state of every preceding fieldset (hidden ones included).
        const firstVisibleIndex = inputWrappers.indexOf(visibleFieldsets[0]);

        inputWrappers.forEach((fieldset, index) => {
            if (fieldset.hasAttribute('hidden') || index === firstVisibleIndex) return;

            const optionInputs = [...fieldset.querySelectorAll('input[type="radio"]')];
            if (!optionInputs.length) return;

            // Collect the checked value from every preceding fieldset.
            // Hidden (single-value) fieldsets are always checked, so they always contribute.
            const precedingSelections = [];
            for (let i = 0; i < index; i++) {
                const checked = inputWrappers[i].querySelector(':checked');
                if (!checked) return; // preceding option not selected yet — skip this fieldset
                precedingSelections.push(checked.value);
            }

            // Find variants that match every preceding selection.
            const matchingVariants = this.getVariantData().filter(variant =>
                precedingSelections.every((val, i) => variant[`option${i + 1}`] === val)
            );

            const availableValues = matchingVariants
                .filter(isEffectivelyAvailable)
                .map(v => v[`option${index + 1}`]);

            this.setInputAvailability(optionInputs, availableValues);
        });
    },

    setInputAvailability: function(listOfOptions, listOfAvailableOptions) {
        listOfOptions.forEach((input) => {
            if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
                input.classList.remove('disabled');
            } else {
                input.classList.add('disabled');
            }
        });
    },

    getAddOnCheckboxProductData: function() {
        const jsonScript = this.el.querySelector('.add-on-checkbox-product-json');

        if (!jsonScript) return null;

        this.addOnCheckboxProductData = this.addOnCheckboxProductData || JSON.parse(this.el.querySelector('.add-on-checkbox-product-json').textContent);
        return this.addOnCheckboxProductData;
    },

    getVariantData: function() {
        if (this.variantData) {
            const existingVariantDataJSON = JSON.stringify(this.variantData);
            const newVariantDataJSON = JSON.stringify(JSON.parse(this.el.querySelector('.variants-json').textContent));
        if (existingVariantDataJSON !== newVariantDataJSON) {
            this.variantData = JSON.parse(newVariantDataJSON);
        }
        } else {
            this.variantData = JSON.parse(this.el.querySelector('.variants-json').textContent);
        }

        return this.variantData;
    },

    updateVariantInput: function() {
        const input = this.el.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
    },

    updatePromoText: function() {
        const wrapper = this.el.closest('.product-core-inner')?.querySelector('.pdp-promo-text-wrapper');
        if (!wrapper) return;
        if (!this.currentVariant) return;

        const variantId = this.currentVariant?.id?.toString();
        const allPromos = wrapper.querySelectorAll('.pdp-promo-text');
        const variantPromos = variantId ? wrapper.querySelectorAll(`[data-promo-variant-id="${variantId}"]`) : null;
        const productPromos = wrapper.querySelectorAll('[data-promo-scope="product"]');

        allPromos.forEach(el => { el.hidden = true; });

        if (variantPromos && variantPromos.length > 0) {
            variantPromos.forEach(el => { el.hidden = false; });
        } else {
            productPromos.forEach(el => { el.hidden = false; });
        }
    },

    updateSubtext: function() {
        const wrapper = this.el.closest('.product-core-inner')?.querySelector('.pdp-subtext-wrapper');
        if (!wrapper) return;
        if (!this.currentVariant) return;

        const variantId = this.currentVariant?.id?.toString();
        const allSubtext = wrapper.querySelectorAll('.block-subtext');
        const variantSubtext = variantId ? wrapper.querySelectorAll(`[data-subtext-variant-id="${variantId}"]`) : null;
        const productSubtext = wrapper.querySelectorAll('[data-subtext-scope="product"]');

        allSubtext.forEach(el => { el.hidden = true; });

        if (variantSubtext && variantSubtext.length > 0) {
            variantSubtext.forEach(el => { el.hidden = false; });
        } else {
            productSubtext.forEach(el => { el.hidden = false; });
        }
    },

    updateBadge: function() {
        const scope = this.el.closest('.product-detail-container') || this.el.closest('.product-core-inner');
        const wrapper = scope?.querySelector('.product-badges');
        if (!wrapper) return;
        if (!this.currentVariant) return;

        const variantId = this.currentVariant?.id?.toString();
        const allBadges = wrapper.querySelectorAll('.product-badge');
        const variantBadges = variantId ? wrapper.querySelectorAll(`[data-badge-variant-id="${variantId}"]`) : null;
        const productBadges = wrapper.querySelectorAll('[data-badge-scope="product"]');

        allBadges.forEach(el => { el.hidden = true; });

        if (variantBadges && variantBadges.length > 0) {
            variantBadges.forEach(el => { el.hidden = false; });
        } else {
            productBadges.forEach(el => { el.hidden = false; });
        }
    },

    toggleAddButton: function(disable = true, text) {
        const productForm = this.el.querySelector('form.add-to-cart-form');
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > .add-to-cart-text');
        if (!addButton) return;

        if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text && addButtonText) addButtonText.textContent = text;
        } else {
            addButton.removeAttribute('disabled');
            if(addButtonText && !addButtonText.textContent.toLowerCase().includes("add to cart")){
                addButtonText.textContent = 'Add to Cart';
            }
        }
    },

    updateURL: function() {
        if (this.currentVariant) {
            window.history.replaceState({}, '', `${window.location.pathname}?variant=${this.currentVariant.id}`);
        }
    },
    /**
     * DD-2804 Bellami Hair - Add AfterPay/ShopPay messaging to PDPs
     * NOTE: We onlh allow afterpay message for US customers
     * @returns void
     */
    updateAfterPayInstallmentAmt: function (){
        // update afterpay installment price if exist
        const afterPayMsg = document.querySelector('[data-afterpay-msg]')
        if(!afterPayMsg){
            return
        }
        const intallPrice = afterPayMsg.querySelector('.after-pay-installment')
        intallPrice.innerHTML = Shopify.formatMoney(this.currentVariant.price / 4 , window.theme.moneyFormat)

    },

	destroy: function() {
		var self = this;
		self.undelegateEvents();
	}

});

// Define the module here
let OneProductForm = {
	init: function (settings) {
		return new OneProductFormView(settings);
	}
}

// Output the module
export default OneProductForm;

// more accurate way to get product inventory
async function getAccurateInventory(currentVariant) {
  const STOREFRONT_ACCESS_TOKEN = "6a43f2663b283f733c932aebb9d8ac85";
  const SHOP_URL = "bellami-dev.myshopify.com";

  if (!currentVariant || !currentVariant.id) {
    console.warn("⚠️ No currentVariant provided.");
    return null;
  }

  const gid = `gid://shopify/ProductVariant/${currentVariant.id}`;

  const query = `
    query($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          quantityAvailable
        }
      }
    }
  `;

  try {
    const response = await fetch(`https://${SHOP_URL}/api/2025-01/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN
      },
      body: JSON.stringify({ query, variables: { ids: [gid] } })
    });

    const json = await response.json();

    if (json.errors) {
      console.error("❌ GraphQL Errors:", json.errors);
      return null;
    }

    const node = json.data.nodes[0];
    if (!node) {
      console.error("❌ No ProductVariant found in response:", json);
      return null;
    }

    currentVariant.accurate_quantity = node.quantityAvailable;
    currentVariant.accurate_available = node.availableForSale;

    return node.quantityAvailable;
  } catch (err) {
    console.error("❌ Fetch failed:", err);
    return null;
  }
}
